import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Form, Formik, type FormikHelpers, useFormikContext } from "formik";
import * as yup from "yup";

import { type ErrorType } from "@vega-api/configs/customClientQuery";
import { getErrorMessage } from "@vega-api/custom/utils/errorHandling";
import { getGetDealReportingMetricsQueryKey } from "@vega-api/generated/coinvest-deal-report/coinvest-deal-report";
import {
  getGetDealReportingFinancialReturnsQueryKey,
  useCreateDealReportingSnapshot,
  useUpdateDealReportingSnapshot,
} from "@vega-api/generated/coinvest-deal-reporting-financial-returns/coinvest-deal-reporting-financial-returns";
import { ThemedButton } from "@vega-core/components/Buttons/ThemedButton";
import { DateField } from "@vega-core/components/Form/Fields/DateField";
import { InputField } from "@vega-core/components/Form/Fields/InputField";
import { TextareaField } from "@vega-core/components/Form/Fields/TextareaField";
import { ValidationErrorText } from "@vega-core/components/Form/Layout/ValidationError";
import { ActionaryModal } from "@vega-core/components/Modals/ActionaryModal/ActionaryModal";
import { useToast } from "@vega-core/components/Toast/useToast";
import { m } from "@vega-core/paraglide/messages";
import { useCoInvestErrorToast } from "@vega-modules/hooks/useCoInvestErrorToast";

import {
  formatPeriodLabel,
  type HistoryRow,
} from "../../utils/formatFinancialReturns";

type MetricValue = number | "";

type FormValues = {
  periodDate: string;
  moicGross: MetricValue;
  irrNet: MetricValue;
  dpi: MetricValue;
  tvpi: MetricValue;
  note: string;
};

type Props = {
  dealId: string;
  onClose: () => void;
  snapshot?: HistoryRow;
};

const metricFields = {
  moicGross: yup
    .string()
    .test(
      "non-negative",
      m.coInvestments_report_snapshotForm_validation_nonNegative(),
      (val) => !val || Number(val) >= 0,
    ),
  irrNet: yup.string(),
  dpi: yup
    .string()
    .test(
      "non-negative",
      m.coInvestments_report_snapshotForm_validation_nonNegative(),
      (val) => !val || Number(val) >= 0,
    ),
  tvpi: yup
    .string()
    .test(
      "non-negative",
      m.coInvestments_report_snapshotForm_validation_nonNegative(),
      (val) => !val || Number(val) >= 0,
    ),
  note: yup.string().max(2000),
};

const atLeastOneMetricOrNote = (
  values: Partial<FormValues> | undefined,
): boolean => {
  if (!values) return false;

  return (
    (["moicGross", "irrNet", "dpi", "tvpi"] as const).some(
      (key) => values[key] !== "" && values[key] !== undefined,
    ) || (values.note ?? "").trim() !== ""
  );
};

const atLeastOneMessage =
  m.coInvestments_report_snapshotForm_validation_atLeastOne();

const createSchema = yup.object({
  periodDate: yup
    .string()
    .required(m.coInvestments_report_snapshotForm_validation_periodRequired()),
  ...metricFields,
});

const editSchema = yup.object({
  periodDate: yup.string(),
  ...metricFields,
});

function AtLeastOneError() {
  const { errors, submitCount } = useFormikContext<FormValues>();
  const error = (errors as Record<string, string>)[""];

  if (!error || submitCount === 0) return null;

  return (
    <ValidationErrorText dataTestId="snapshot-form-at-least-one">
      {error}
    </ValidationErrorText>
  );
}

const parseMetric = (val: MetricValue): number | undefined =>
  val === "" ? undefined : val;

export function SnapshotFormModal({ dealId, onClose, snapshot }: Props) {
  const isEdit = snapshot !== undefined;
  const queryClient = useQueryClient();
  const showToast = useToast();
  const showErrorToast = useCoInvestErrorToast();

  const { mutateAsync: createSnapshot, isPending: isCreating } =
    useCreateDealReportingSnapshot();
  const { mutateAsync: updateSnapshot, isPending: isUpdating } =
    useUpdateDealReportingSnapshot();

  const isPending = isCreating || isUpdating;

  // Time is zeroed (UTC) so the default can never be a future timestamp, which the BE rejects.
  const [defaultPeriodDate] = useState(() => {
    const today = new Date();
    today.setUTCHours(0, 0, 0, 0);

    return today.toISOString();
  });

  const initialValues: FormValues = isEdit
    ? {
        periodDate: snapshot.periodDate,
        moicGross: snapshot.moicGross ?? "",
        irrNet: snapshot.irrNet ?? "",
        dpi: snapshot.dpi ?? "",
        tvpi: snapshot.tvpi ?? "",
        note: snapshot.note ?? "",
      }
    : {
        periodDate: defaultPeriodDate,
        moicGross: "",
        irrNet: "",
        dpi: "",
        tvpi: "",
        note: "",
      };

  const invalidateQueries = () => {
    queryClient.invalidateQueries({
      queryKey: getGetDealReportingFinancialReturnsQueryKey(dealId),
    });
    queryClient.invalidateQueries({
      queryKey: getGetDealReportingMetricsQueryKey(dealId),
    });
  };

  const handleSubmit = async (
    values: FormValues,
    { setFieldError }: FormikHelpers<FormValues>,
  ) => {
    try {
      if (isEdit) {
        await updateSnapshot({
          dealId,
          snapshotId: snapshot.snapshotId,
          data: {
            moicGross: parseMetric(values.moicGross),
            irrNet: parseMetric(values.irrNet),
            dpi: parseMetric(values.dpi),
            tvpi: parseMetric(values.tvpi),
            note: values.note,
          },
        });
        showToast({
          variant: "success",
          title: m.coInvestments_report_snapshotForm_success_edit(),
        });
      } else {
        await createSnapshot({
          dealId,
          data: {
            periodDate: values.periodDate,
            moicGross: parseMetric(values.moicGross),
            irrNet: parseMetric(values.irrNet),
            dpi: parseMetric(values.dpi),
            tvpi: parseMetric(values.tvpi),
            note: values.note || undefined,
          },
        });
        showToast({
          variant: "success",
          title: m.coInvestments_report_snapshotForm_success_create(),
        });
      }
      invalidateQueries();
      onClose();
    } catch (error) {
      const { statusCode } = getErrorMessage(error as ErrorType<unknown>);

      if (!isEdit && statusCode === 409) {
        setFieldError(
          "periodDate",
          m.coInvestments_report_snapshotForm_error_duplicate(),
        );
      } else {
        showErrorToast(error as ErrorType<unknown>);
      }
    }
  };

  return (
    <ActionaryModal
      dataTestId="snapshot-form-modal"
      fluidHeight
      open
      setOpen={onClose}
      title={
        isEdit
          ? m.coInvestments_report_snapshotForm_editTitle()
          : m.coInvestments_report_snapshotForm_createTitle()
      }
    >
      <Formik
        enableReinitialize
        initialValues={initialValues}
        onSubmit={handleSubmit}
        validate={(values) => {
          if (!atLeastOneMetricOrNote(values)) {
            return { "": atLeastOneMessage };
          }

          return {};
        }}
        validationSchema={isEdit ? editSchema : createSchema}
      >
        <Form className="space-y-4">
          {isEdit ? (
            <div className="flex flex-col gap-1">
              <span className="body-small-semibold text-text/secondary">
                {m.coInvestments_report_snapshotForm_period()}
              </span>
              <span className="body-base-regular text-text/primary">
                {formatPeriodLabel(snapshot.periodDate)}
              </span>
            </div>
          ) : (
            <DateField
              disableFuture
              label={m.coInvestments_report_snapshotForm_period()}
              name="periodDate"
            />
          )}

          <div className="grid grid-cols-2 gap-4">
            <InputField
              isOptional
              label={m.coInvestments_report_moicGross()}
              name="moicGross"
              type="number"
            />
            <InputField
              isOptional
              label={m.coInvestments_report_irrNet()}
              name="irrNet"
              type="number"
            />
            <InputField
              isOptional
              label={m.coInvestments_report_dpi()}
              name="dpi"
              type="number"
            />
            <InputField
              isOptional
              label={m.coInvestments_report_tvpi()}
              name="tvpi"
              type="number"
            />
          </div>

          <TextareaField
            isOptional
            label={m.coInvestments_report_snapshotForm_note()}
            name="note"
          />

          <AtLeastOneError />

          <div className="flex justify-end gap-3 pt-2">
            <ThemedButton
              dataTestId="snapshot-form-cancel"
              onClick={onClose}
              size="default"
              type="button"
              variant="secondary"
            >
              {m.btn_cancel()}
            </ThemedButton>
            <ThemedButton
              dataTestId="snapshot-form-submit"
              disabled={isPending}
              size="default"
              type="submit"
              variant="primary"
            >
              {m.btn_save()}
            </ThemedButton>
          </div>
        </Form>
      </Formik>
    </ActionaryModal>
  );
}
